import axios, { type InternalAxiosRequestConfig } from 'axios'

// Access token lives in memory only (not localStorage) so XSS can't read it from
// storage. A page reload loses it; the httpOnly refresh cookie mints a new one.
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

// Drop the token a previous version persisted.
try {
  localStorage.removeItem('immo-web-token')
} catch {
  // storage unavailable
}

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let refreshPromise: Promise<string> | null = null

function requestRefresh(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = api
      .post<{ token: string }>('/auth/refresh')
      .then((res) => res.data.token)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

function logoutAndRedirect() {
  setAccessToken(null)
  if (window.location.pathname !== '/login') {
    window.location.assign('/login')
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config as RetriableConfig | undefined
    const noRetryEndpoints = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout']
    const skipRetry = noRetryEndpoints.some((p) => config?.url?.startsWith(p))

    if (err.response?.status === 401 && config && !config._retry && !skipRetry) {
      config._retry = true
      try {
        const token = await requestRefresh()
        setAccessToken(token)
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
        return api(config)
      } catch {
        logoutAndRedirect()
        return Promise.reject(err)
      }
    }

    if (err.response?.status === 401 && !skipRetry) {
      logoutAndRedirect()
    }
    return Promise.reject(err)
  },
)

export function apiErrorMessage(err: unknown, fallback = 'Une erreur est survenue'): string {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error || err.message || fallback
  }
  return fallback
}

export default api
